import {
  DatePicker,
  DayOfWeek,
  defaultDatePickerStrings,
} from "@fluentui/react";
import {
  Button,
  Dropdown,
  Label,
  Option,
  mergeClasses,
} from "@fluentui/react-components";
import { format } from "date-fns";
import { useId, type FC } from "react";
import { SORT_OPTIONS } from "../constants";
import { NotificationFilterDropdowns } from "../NotificationFilterDropdowns";
import { NotificationFilterState, NotificationSort } from "../types";
import { useNotificationFiltersStyles } from "./styles";

const formatDate = (date?: Date): string =>
  date ? format(date, "dd-MM-yy") : "";

interface NotificationHistoryFiltersProps {
  filters: NotificationFilterState;
  sort: NotificationSort;
  fromDate: Date | null;
  toDate: Date | null;
  currentDate: Date;
  onFiltersChange: (nextFilters: NotificationFilterState) => void;
  onSortChange: (nextSort: NotificationSort) => void;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  onReset: () => void;
}

const NotificationHistoryFilters: FC<NotificationHistoryFiltersProps> = ({
  filters,
  sort,
  fromDate,
  toDate,
  currentDate,
  onFiltersChange,
  onSortChange,
  onFromDateChange,
  onToDateChange,
  onReset,
}) => {
  const styles = useNotificationFiltersStyles();
  const fromDateInputId = useId();
  const toDateInputId = useId();
  const sortDropdownId = useId();

  return (
    <section
      className={styles.filtersRow}
      aria-label="Notification filters and sorting"
    >
      <div className={styles.filterGroup}>
        <NotificationFilterDropdowns
          filters={filters}
          onChange={onFiltersChange}
          showTimeFilter={false}
          classNames={{
            field: styles.filterField,
            dropdown: styles.dropdown,
          }}
        />

        <div className={styles.dateRange} aria-label="Notification date range">
          <div className={mergeClasses(styles.filterField, styles.dateField)}>
            <Label htmlFor={fromDateInputId}>From</Label>
            <DatePicker
              textField={{ id: fromDateInputId }}
              today={currentDate}
              highlightCurrentMonth
              highlightSelectedMonth
              showMonthPickerAsOverlay
              showGoToToday={false}
              firstDayOfWeek={DayOfWeek.Sunday}
              strings={defaultDatePickerStrings}
              placeholder="DD-MM-YY"
              formatDate={formatDate}
              maxDate={toDate ?? currentDate}
              onSelectDate={(date) => onFromDateChange(date ?? null)}
              value={fromDate ?? undefined}
            />
          </div>

          <div className={mergeClasses(styles.filterField, styles.dateField)}>
            <Label htmlFor={toDateInputId}>To</Label>
            <DatePicker
              textField={{ id: toDateInputId }}
              today={currentDate}
              highlightCurrentMonth
              highlightSelectedMonth
              showMonthPickerAsOverlay
              showGoToToday={false}
              firstDayOfWeek={DayOfWeek.Sunday}
              strings={defaultDatePickerStrings}
              placeholder="DD-MM-YY"
              formatDate={formatDate}
              minDate={fromDate ?? currentDate}
              onSelectDate={(date) => onToDateChange(date ?? null)}
              value={toDate ?? currentDate}
            />
          </div>
        </div>
      </div>

      <div className={mergeClasses(styles.filterField, styles.sortField)}>
        <Label htmlFor={sortDropdownId}>Sort by</Label>
        <Dropdown
          id={sortDropdownId}
          className={styles.sortDropdown}
          value={SORT_OPTIONS.find(({ value }) => value === sort)?.label}
          selectedOptions={[sort]}
          onOptionSelect={(_event, data) =>
            onSortChange(data.optionValue as NotificationSort)
          }
        >
          {SORT_OPTIONS.map(({ value, label }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </Dropdown>
      </div>

      <Button
        className={styles.resetButton}
        onClick={onReset}
        appearance="transparent"
      >
        Reset
      </Button>
    </section>
  );
};

export default NotificationHistoryFilters;
